import { motion } from "framer-motion";
import Layout from "@/components/Layout";
// SEO: Import reusable SEO component to add organization / about page metadata.
import SEO from '@/components/SEO';
import { Button } from "@/components/ui/button";
import { Link } from "wouter";

export default function About() {
  const features = [
    {
      title: "Real Market Data",
      description:
        "The system aggregates price action, trends, and market behavior to build a live analysis context.",
    },
    {
      title: "Innovative AI Models",
      description:
        "Algorithms evaluate patterns, compare prior forecasts, and provide structured data metrics.",
    },
    {
      title: "Actionable Insights",
      description:
        "Receive structured analysis: suggested data points for position initiation, risk management parameters, target data points for position closure, and model confidence metrics.",
    },
    {
      title: "Fair Token System",
      description:
        "If the market context isn’t favorable, your token may be returned under service rules.",
    },
  ];

  const benefits = [
    {
      label: "Save Time",
      text: "Reduce manual chart monitoring with structured analysis.",
    },
    {
      label: "Improve Decision Quality",
      text: "Focus on high-confidence analysis insights and research reports.",
    },
    {
      label: "Risk Awareness",
      text: "Analysis highlights context and potential constraints; trading risk remains with the user.",
    },
    {
      label: "Stay Transparent",
      text: "Pay only for validated analysis outputs and market data points.",
    },
  ];

  // JSON-LD Organization schema to reinforce brand authority (EEAT signals).
  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': 'TradeSagePro',
    'url': 'https://tradesagepro.com/about',
    'logo': 'https://tradesagepro.com/favicon.svg',
    'description': 'AI-powered market research and data analysis platform providing structured insights and market intelligence.',
    'sameAs': [
      'https://twitter.com/TradeSagePro'
    ]
  };

  return (
    <Layout>
      {/* SEO TAGS: About page clarifies mission & tech; Organization schema boosts brand entity recognition. */}
      <SEO
        title="About TradeSagePro | AI Market Research & Analysis Platform"
        description="Learn how TradeSagePro uses advanced AI models to deliver data-driven analysis insights, including suggested data points for initiation, risk management parameters, and target closure parameters — with transparent methodology."
        canonical="https://tradesagepro.com/about"
        type="article"
        jsonLd={orgSchema}
      />
      {/* END SEO TAGS */}
      <section className="relative overflow-hidden bg-[hsl(226,26%,7%)]">
        <div className="pointer-events-none absolute -top-24 -left-24 w-[28rem] h-[28rem] rounded-full bg-[hsl(193,100%,50%,0.08)] blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-[hsl(260,100%,70%,0.06)] blur-3xl" />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-flex items-center rounded-full border border-[hsl(193,100%,50%,0.35)] bg-[hsl(193,100%,50%,0.08)] text-[hsl(193,100%,70%)] px-3 py-1 text-xs font-medium">
              TradeSagePro: The AI Companion for Data-Driven Market Research
            </span>
            <h1 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight">
              TradeSagePro: The AI Companion for Data-Driven Market Research
            </h1>
            <p className="mt-4 text-[hsl(210,9%,80%)] text-base sm:text-lg">
              TradeSagePro was created with one simple goal — to provide analysis insights, transparency, and simplicity in a complex market environment.
            </p>
            <p className="mt-2 text-[hsl(210,9%,78%)] text-sm sm:text-base">
              Markets are full of noise: excessive alerts, inaccurate forecasts, and decisions often driven by emotion. That’s why we built TradeSagePro — an AI-powered research tool that analyzes the market in real time and delivers only analysis outputs confirmed with high confidence.
            </p>
          </motion.div>

          <div className="mt-10 grid sm:grid-cols-2 gap-6 lg:gap-8">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="rounded-xl border border-gray-800 bg-[hsl(226,26%,9%)] p-5 sm:p-6 hover:border-[hsl(193,100%,50%)]/40 transition-colors"
              >
                <h3 className="text-white text-lg sm:text-xl font-semibold">
                  {f.title}
                </h3>
                <p className="mt-2 text-[hsl(210,9%,80%)] text-sm sm:text-base">{f.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-10 sm:mt-14 rounded-2xl border border-gray-800 bg-gradient-to-b from-[hsl(226,26%,9%)] to-[hsl(226,26%,7%)] p-6 sm:p-8"
          >
            <div className="grid lg:grid-cols-2 gap-6 lg:gap-10 items-start">
              <div>
                <h2 className="text-white text-xl sm:text-2xl font-bold">Optimize Decisions with AI-Powered Market Context</h2>
                <ul className="mt-4 space-y-3">
                  {benefits.map((b) => (
                    <li key={b.label} className="flex items-start gap-3">
                      <span className="mt-1 inline-block h-2 w-2 rounded-full bg-[hsl(193,100%,50%)]" />
                      <div>
                        <div className="text-white font-medium">{b.label}</div>
                        <div className="text-[hsl(210,9%,80%)] text-sm sm:text-base">{b.text}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="relative rounded-xl border border-gray-800 bg-[hsl(226,26%,10%)] p-5">
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-tr from-[hsl(193,100%,50%,0.05)] to-transparent pointer-events-none" />
                  <h3 className="text-white font-semibold">Our Mission</h3>
                  <p className="mt-3 text-[hsl(210,9%,80%)] text-sm sm:text-base">
                    Our mission is to make market research clearer, responsible, and accessible. TradeSagePro — where AI supports data-driven analysis.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-center gap-3 relative z-10">
              <Button
                asChild
                variant="outline"
                className="w-full sm:w-auto border-gray-700 text-white hover:bg-white/5"
              >
                <Link href="/how-it-works">See How It Works</Link>
              </Button>
              <Button
                asChild
                className="w-full sm:w-auto bg-[hsl(193,100%,50%)] text-[hsl(226,26%,7%)] hover:scale-[1.02] transition"
              >
                <Link href="/pricing">View Pricing</Link>
              </Button>
            </div>
          </motion.div>
          
        </div>
      </section>
    </Layout>
  );
}
